import { Injectable } from '@angular/core';
import { _Service } from './_service';
import { AppId, NewsArticle } from '../../model';
import { buildQuery } from '../../util';

interface CountResult {
  risk?: AppId;
  locations: AppId[];
  count: number;
  date: Date;
  news: AppId[];
}

@Injectable({ providedIn: 'root' })
export class NewsArticleService extends _Service<NewsArticle> {
  constructor() {
    super('api/news-article');
  }

  import = async (id: string) => this.http.post(`api/news-article/import-google/${id}`, null);
  count = async (data: { risk?: AppId; locations: AppId[] }[]) =>
    (await this.http.post<CountResult[]>('api/news-article/count', data)).map((entry) => ({
      ...entry,
      locations: entry.locations.sort(),
    }));
  previewImage = async (url: string) => this.http.getText('api/news-article/preview-image' + buildQuery({ url }));

  // --- Snapshot archival ---
  archiveThumbUrl = (id: AppId) => `api/news-article/${this.mapId(id)}/archive/thumb`;
  archiveUrl = (id: AppId) => `api/news-article/${this.mapId(id)}/archive`;
  archiveFileUrl = (id: AppId) => `api/news-article/${this.mapId(id)}/archive/file`;
  /** URL of full-snapshot tile `i` (0-based; the full page is stored as N vertical WebP tiles). */
  archiveSegmentUrl = (id: AppId, i: number) => `api/news-article/${this.mapId(id)}/archive/segment/${i}`;

  /** Enable/Refresh: synchronous capture (force=true). A warn/fail returns 200 with disabled=true. */
  refreshArchive = async (id: AppId) => this.http.post<NewsArticle>(this.archiveUrl(id), null);
  /** Admin visibility override. true → flip flag; false → re-capture (equivalent to Enable). */
  setDisabled = async (id: AppId, disabled: boolean) =>
    this.http.put<NewsArticle>(`api/news-article/${this.mapId(id)}/disabled`, { disabled });
  /** Batched (≤5) backfill of never-attempted rated articles; loop until it returns []. */
  archiveBackfill = async () => this.http.post<BackfillRow[]>('api/admin/news-article/archive-backfill', null);
  /** Batched (≤5) migration of legacy thumbnails into the store (flags incomplete); loop until []. */
  legacyThumbMigrate = async () =>
    this.http.post<{ articleId: string; incomplete: boolean; thumbWritten: boolean }[]>(
      'api/admin/news-article/legacy-thumb-migrate',
      null
    );

  // Authenticated binary GETs (the Bearer interceptor only runs through HttpClient, not <img src>).
  archiveThumbBlob = async (id: AppId) => (await this.http.getBlob(this.archiveThumbUrl(id))).body!;
  archiveFullBlob = async (id: AppId) => (await this.http.getBlob(this.archiveUrl(id))).body!;
  /** Authenticated blob GET of full-snapshot tile `i` (the Bearer interceptor runs through HttpClient, not <img src>). */
  archiveSegmentBlob = async (id: AppId, i: number) => (await this.http.getBlob(this.archiveSegmentUrl(id, i))).body!;
  archiveFileBlob = async (id: AppId) => (await this.http.getBlob(this.archiveFileUrl(id))).body!;
}

export interface BackfillRow {
  articleId: string;
  url: string | null;
  status: string;
}
